import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid,
    Autocomplete, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Box, CircularProgress, Typography, Tabs, Tab, Alert, Divider, Checkbox
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import EditNoteIcon from '@mui/icons-material/EditNote';

import { useReactToPrint } from 'react-to-print';

import { getInvestidores, getFornecedores, getObraById, getFornecedorById } from '../services/api';
import { Lancamento, Fornecedor, Investidor, Obra } from '../services/types';
import ReciboPrint, { Pagador, ReciboItem } from './ReciboPrint';
import dayjs from 'dayjs';
import { supabase } from '../services/supabaseClient';
import { gerarLinkAssinatura, getAssinaturaByHashCurto, getAssinaturaAtivaByLancamentoId } from '../services/assinaturaService';
import { updateFornecedor } from '../services/fornecedorService';
import { generateReciboPDFBlob } from '../services/pdfGenerator';

interface ReciboLancamentoModalProps {
    open: boolean;
    onClose: () => void;
    lancamento: Lancamento | null;
    onFornecedorUpdated?: () => void;
}

const ReciboLancamentoModal: React.FC<ReciboLancamentoModalProps> = ({ open, onClose, lancamento, onFornecedorUpdated }) => {
    const [loading, setLoading] = useState(true);
    const [modo, setModo] = useState<'imprimir' | 'assinatura'>('imprimir');
    
    const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
    const [investidores, setInvestidores] = useState<Investidor[]>([]);
    
    const [selectedFornecedor, setSelectedFornecedor] = useState<Fornecedor | null>(null);
    const [selectedInvestidor, setSelectedInvestidor] = useState<Investidor | null>(null);
    const [selectedObra, setSelectedObra] = useState<Obra | null>(null);

    const [data, setData] = useState<string>('');
    const [cidade, setCidade] = useState<string>('Rio das Ostras');
    const [estado, setEstado] = useState<string>('RJ');
    const [tipoDocumento, setTipoDocumento] = useState<'CPF' | 'CNPJ'>('CNPJ');

    const [pagadorInterveniente, setPagadorInterveniente] = useState<boolean>(false);
    const [selectedPagador, setSelectedPagador] = useState<Pagador | null>(null);
    
    const [quitaIntegralmente, setQuitaIntegralmente] = useState<boolean>(true);
    const [tipoRecibo, setTipoRecibo] = useState<'servico' | 'material'>('servico');

    const [emailAssinante, setEmailAssinante] = useState<string>('');
    const [telefoneAssinante, setTelefoneAssinante] = useState<string>('');
    const [enviarEmail, setEnviarEmail] = useState<boolean>(true);
    const [enviarSms, setEnviarSms] = useState<boolean>(false);
    
    const [linkAssinatura, setLinkAssinatura] = useState<string | null>(null);
    const [hashCurto, setHashCurto] = useState<string | null>(null);
    const [assinaturaStatus, setAssinaturaStatus] = useState<'AGUARDANDO' | 'ASSINADO' | null>(null);
    const [gerandoLink, setGerandoLink] = useState(false);
    const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);

    const componentRef = useRef<HTMLDivElement>(null);

    // Polling effect para verificar assinatura
    useEffect(() => {
        let intervalId: NodeJS.Timeout;
        if (assinaturaStatus === 'AGUARDANDO' && hashCurto) {
            intervalId = setInterval(async () => {
                try {
                    const data = await getAssinaturaByHashCurto(hashCurto);
                    if (data && data.status === 'ASSINADO') {
                        setAssinaturaStatus('ASSINADO');
                        clearInterval(intervalId);
                    }
                } catch (error) {
                    // Ignore errors during polling
                }
            }, 5000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [assinaturaStatus, hashCurto]);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Recibo_${selectedFornecedor?.nome_razao_social || 'Lancamento'}_${data}`,
    });

    useEffect(() => {
        if (!open || !lancamento) {
            setSelectedInvestidor(null);
            setPagadorInterveniente(false);
            setSelectedPagador(null);
            setModo('imprimir');
            setLinkAssinatura(null);
            setHashCurto(null);
            setAssinaturaStatus(null);
            setEmailAssinante('');
            setTelefoneAssinante('');
            setEnviarEmail(true);
            setEnviarSms(false);
            setUpdateSuccess(false);
            return;
        }
        
        setData(lancamento.data_lancamento || dayjs().format('YYYY-MM-DD'));
        
        const loadData = async () => {
            setLoading(true);
            try {
                const [fornecedoresData, investidoresData] = await Promise.all([
                    getFornecedores(),
                    getInvestidores()
                ]);
                setFornecedores(fornecedoresData || []);
                setInvestidores(investidoresData || []);

                const [obraData, fornecedorData] = await Promise.all([
                    getObraById(lancamento.obra_id),
                    lancamento.fornecedor_id ? getFornecedorById(lancamento.fornecedor_id) : Promise.resolve(null)
                ]);

                setSelectedObra(obraData);
                setSelectedFornecedor(fornecedorData);

                setSelectedInvestidor(null);
                setTipoRecibo(fornecedorData?.tipo === 'MATERIAL' ? 'material' : 'servico');
                
                // Verificar se já existe assinatura ativa
                try {
                    const assinaturaAtiva = await getAssinaturaAtivaByLancamentoId(lancamento.id);
                    if (assinaturaAtiva && assinaturaAtiva.hash_curto) {
                        const domain = window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://assinaturas-sistema.com.br';
                        setLinkAssinatura(`${domain}/s/${assinaturaAtiva.hash_curto}`);
                        setHashCurto(assinaturaAtiva.hash_curto);
                        setAssinaturaStatus(assinaturaAtiva.status === 'PENDENTE' ? 'AGUARDANDO' : 'ASSINADO');
                        setModo('assinatura');
                    }
                } catch (e) {
                    console.error("Erro ao verificar assinatura ativa", e);
                }

            } catch (error) {
                console.error("Erro ao carregar dados do recibo", error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [open, lancamento]);

    useEffect(() => {
        if (selectedFornecedor) {
            const doc = selectedFornecedor.cnpj_cpf.replace(/\D/g, '');
            setTipoDocumento(doc.length > 11 ? 'CNPJ' : 'CPF');
            if (selectedFornecedor.email) setEmailAssinante(selectedFornecedor.email);
            if (selectedFornecedor.telefone) setTelefoneAssinante(selectedFornecedor.telefone);
        }
    }, [selectedFornecedor]);

    const handleGerarAssinatura = async () => {
        if (!lancamento || !componentRef.current) return;
        setGerandoLink(true);
        try {

            const totalValor = lancamento.valor_total_liquido || 0;
            const mainDescricao = lancamento.items.map(i => i.descricao).join(', ');

            const pdfBlob = generateReciboPDFBlob(
                selectedFornecedor!,
                selectedInvestidor!,
                selectedObra?.unidades?.find(u => u.id === lancamento.unidade_id),
                selectedObra,
                totalValor,
                new Date(),
                mainDescricao,
                selectedObra?.municipio || cidade || 'Cidade',
                tipoDocumento,
                pagadorInterveniente && selectedPagador ? {
                    id: Number(selectedPagador.id) || 999,
                    nome: selectedPagador.nome,
                    documento: selectedPagador.documento,
                    tipo: 'Fornecedor'
                } : null,
                quitaIntegralmente,
                tipoRecibo,
                lancamento.items || []
            );

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("Usuário não autenticado");

            const pathName = `${user.id}/rascunho_assinatura_${lancamento.id}_${Date.now()}.pdf`;
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('anexos')
                .upload(pathName, pdfBlob, {
                    contentType: 'application/pdf'
                });

            if (uploadError) throw uploadError;
            
            // Check if we need to update Fornecedor's contact info
            if (selectedFornecedor && selectedFornecedor.id) {
                let updates: any = {};
                if (enviarEmail && emailAssinante && emailAssinante !== selectedFornecedor.email) {
                    updates.email = emailAssinante;
                }
                if (enviarSms && telefoneAssinante && telefoneAssinante !== selectedFornecedor.telefone) {
                    updates.telefone = telefoneAssinante;
                }
                if (Object.keys(updates).length > 0) {
                    try {
                        await updateFornecedor(selectedFornecedor.id, updates);
                        setUpdateSuccess(true);
                        if (onFornecedorUpdated) onFornecedorUpdated();
                    } catch (updateError) {
                        console.error('Erro ao atualizar fornecedor', updateError);
                    }
                }
            }
            
            const metodoEnvio = enviarEmail && enviarSms ? 'ambos' : (enviarSms ? 'sms' : 'email');
            const nomeDoAssinante = selectedFornecedor?.nome_razao_social || 'Fornecedor';

            const { link, hash_curto } = await gerarLinkAssinatura(lancamento.id, emailAssinante, nomeDoAssinante, uploadData.path, telefoneAssinante, metodoEnvio);
            setLinkAssinatura(link);
            setHashCurto(hash_curto);
            setAssinaturaStatus('AGUARDANDO');
        } catch (error) {
            console.error('Erro ao gerar assinatura', error);
            alert('Erro ao gerar link de assinatura.');
        } finally {
            setGerandoLink(false);
        }
    };

    const isFornecedorMaterial = (f: Fornecedor) => !f.tipo || f.tipo === 'MATERIAL' || f.tipo === 'AMBOS';
    const fornecedoresMaterial = fornecedores.filter(isFornecedorMaterial);

    const pagadoresPossiveis: Pagador[] = [
        ...investidores
            .filter(i => !selectedInvestidor || i.id !== selectedInvestidor.id)
            .map(i => ({ id: i.id, nome: i.nome, documento: i.cpf_cnpj, tipo: 'Investidor' as const })),
        ...fornecedoresMaterial.map(f => ({ id: f.id, nome: f.nome_razao_social, documento: f.cnpj_cpf, tipo: 'Fornecedor' as const }))
    ];

    if (!lancamento) return null;

    const totalValor = lancamento.valor_total_liquido || 0;
    
    const itensRecibo: ReciboItem[] = lancamento.items.map(i => ({
        id: i.id.toString(),
        descricao: i.descricao,
        quantidade: i.quantidade,
        valor: i.valor_liquido_proporcional || (i.valor_unitario * i.quantidade)
    }));

    const mainDescricao = lancamento.items.map(i => i.descricao).join(', ');

    const isFormValid = selectedFornecedor && selectedInvestidor && selectedObra && totalValor > 0 && (!pagadorInterveniente || selectedPagador);
    const isMetodoSelecionado = enviarEmail || enviarSms;
    const isEmailValid = !enviarEmail || (emailAssinante && emailAssinante.includes('@'));
    const isSmsValid = !enviarSms || telefoneAssinante.length > 8;
    const isAssinaturaValid = isFormValid && isMetodoSelecionado && isEmailValid && isSmsValid;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pb: 0 }}>
                <Tabs value={modo} onChange={(e, v) => setModo(v)} aria-label="modos de recibo">
                    <Tab label="Imprimir Recibo" value="imprimir" />
                    <Tab label="Assinatura Digital" value="assinatura" />
                </Tabs>
            </DialogTitle>
            <DialogContent dividers sx={{ backgroundColor: '#f9fafa' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 1 }}>
                        
                        {/* Seção 1: Participantes */}
                        <Box>
                            <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
                                Participantes
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Autocomplete
                                        fullWidth
                                        options={investidores}
                                        getOptionLabel={(option) => option.nome}
                                        value={selectedInvestidor}
                                        onChange={(_, newValue) => setSelectedInvestidor(newValue)}
                                        renderInput={(params) => <TextField {...params} label="Comprador (Investidor) *" required error={!selectedInvestidor} sx={{ bgcolor: 'white' }} />}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Autocomplete
                                        fullWidth
                                        options={fornecedores}
                                        getOptionLabel={(option) => option.nome_razao_social}
                                        value={selectedFornecedor}
                                        onChange={(_, newValue) => setSelectedFornecedor(newValue)}
                                        renderInput={(params) => <TextField {...params} label="Fornecedor (Beneficiário) *" required sx={{ bgcolor: 'white' }} />}
                                        disabled // Carregado do Lançamento
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        {/* Seção 2: Configurações do Recibo */}
                        <Box>
                            <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
                                Configurações do Recibo
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>Tipo de Recibo</FormLabel>
                                        <RadioGroup row value={tipoRecibo} onChange={(e) => setTipoRecibo(e.target.value as 'servico' | 'material')}>
                                            <FormControlLabel value="servico" control={<Radio size="small" />} label={<Typography variant="body2">Serviço</Typography>} />
                                            <FormControlLabel value="material" control={<Radio size="small" />} label={<Typography variant="body2">Material</Typography>} />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>Doc. Beneficiário</FormLabel>
                                        <RadioGroup row value={tipoDocumento} onChange={(e) => setTipoDocumento(e.target.value as 'CPF' | 'CNPJ')}>
                                            <FormControlLabel value="CPF" control={<Radio size="small" />} label={<Typography variant="body2">CPF</Typography>} />
                                            <FormControlLabel value="CNPJ" control={<Radio size="small" />} label={<Typography variant="body2">CNPJ</Typography>} />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>Quita Integralmente?</FormLabel>
                                        <RadioGroup row value={quitaIntegralmente ? 'Sim' : 'Não'} onChange={(e) => setQuitaIntegralmente(e.target.value === 'Sim')}>
                                            <FormControlLabel value="Sim" control={<Radio size="small" />} label={<Typography variant="body2">Sim</Typography>} />
                                            <FormControlLabel value="Não" control={<Radio size="small" />} label={<Typography variant="body2">Não</Typography>} />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>Pagador Interveniente?</FormLabel>
                                        <RadioGroup row value={pagadorInterveniente ? 'Sim' : 'Não'} onChange={(e) => {
                                            const isSim = e.target.value === 'Sim';
                                            setPagadorInterveniente(isSim);
                                            if (!isSim) setSelectedPagador(null);
                                        }}>
                                            <FormControlLabel value="Não" control={<Radio size="small" />} label={<Typography variant="body2">Não</Typography>} />
                                            <FormControlLabel value="Sim" control={<Radio size="small" />} label={<Typography variant="body2">Sim</Typography>} />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                {pagadorInterveniente && (
                                    <Grid size={{ xs: 12 }}>
                                        <Box sx={{ mt: 1 }}>
                                            <Autocomplete
                                                fullWidth
                                                options={pagadoresPossiveis}
                                                getOptionLabel={(option) => `${option.nome} (${option.tipo})`}
                                                value={selectedPagador}
                                                onChange={(_, newValue) => setSelectedPagador(newValue)}
                                                renderInput={(params) => <TextField {...params} label="Nome do Pagador Interveniente *" required error={!selectedPagador} sx={{ bgcolor: 'white' }} />}
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        <Divider />

                        {/* Seção 3: Local e Data */}
                        <Box>
                            <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
                                Local e Data
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 5 }}>
                                    <TextField
                                        label="Cidade"
                                        value={cidade}
                                        onChange={(e) => setCidade(e.target.value)}
                                        fullWidth
                                        sx={{ bgcolor: 'white' }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 3 }}>
                                    <TextField
                                        label="Estado"
                                        value={estado}
                                        onChange={(e) => setEstado(e.target.value)}
                                        fullWidth
                                        sx={{ bgcolor: 'white' }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        label="Data do Recibo"
                                        type="date"
                                        value={data}
                                        onChange={(e) => setData(e.target.value)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ max: "9999-12-31" }}
                                        sx={{ bgcolor: 'white' }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Seção 4: Assinatura Digital */}
                        {modo === 'assinatura' && (
                            <>
                                <Divider />
                                <Box sx={{ 
                                    p: 2.5, 
                                    bgcolor: 'rgba(25, 118, 210, 0.04)', 
                                    borderRadius: 2, 
                                    border: '1px solid', 
                                    borderColor: 'primary.light' 
                                }}>
                                    <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
                                        Dados da Assinatura Digital
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12 }}>
                                            <FormControl component="fieldset">
                                                <FormLabel component="legend" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mb: 1, color: 'text.primary' }}>Como deseja enviar o link?</FormLabel>
                                                <Box sx={{ display: 'flex', gap: 3 }}>
                                                    <FormControlLabel
                                                        control={<Checkbox checked={enviarEmail} onChange={(e) => setEnviarEmail(e.target.checked)} />}
                                                        label="E-mail"
                                                    />
                                                    <FormControlLabel
                                                        control={<Checkbox checked={enviarSms} onChange={(e) => setEnviarSms(e.target.checked)} />}
                                                        label="SMS (Celular)"
                                                    />
                                                </Box>
                                                {(!enviarEmail && !enviarSms) && <Typography color="error" variant="caption">Selecione pelo menos um método de envio.</Typography>}
                                            </FormControl>
                                        </Grid>
                                        
                                        {enviarEmail && (
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    label="E-mail do Assinante *"
                                                    type="email"
                                                    value={emailAssinante}
                                                    onChange={(e) => setEmailAssinante(e.target.value)}
                                                    fullWidth
                                                    error={!emailAssinante || !emailAssinante.includes('@')}
                                                    sx={{ bgcolor: 'white' }}
                                                />
                                            </Grid>
                                        )}
                                        
                                        {enviarSms && (
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    label="Telefone (SMS) *"
                                                    type="tel"
                                                    value={telefoneAssinante}
                                                    onChange={(e) => setTelefoneAssinante(e.target.value)}
                                                    fullWidth
                                                    error={!telefoneAssinante || telefoneAssinante.length < 9}
                                                    sx={{ bgcolor: 'white' }}
                                                    placeholder="(11) 99999-9999"
                                                    helperText="Apenas números ou com DDD"
                                                />
                                            </Grid>
                                        )}
                                        
                                        {(enviarEmail || enviarSms) && (
                                            <Grid size={{ xs: 12 }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    * Dica: Se você alterar o E-mail ou Telefone (SMS) aqui, o cadastro do fornecedor será atualizado no sistema.
                                                </Typography>
                                            </Grid>
                                        )}
                                        {linkAssinatura && (
                                            <Grid size={{ xs: 12 }}>
                                                {assinaturaStatus === 'ASSINADO' ? (
                                                    <Alert 
                                                        severity="success"
                                                        variant="filled"
                                                    >
                                                        O recibo foi assinado digitalmente com sucesso!
                                                    </Alert>
                                                ) : (
                                                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                                        <CircularProgress size={40} color="primary" />
                                                        <Typography variant="h6" color="primary">Aguardando assinatura do fornecedor...</Typography>
                                                        <Typography variant="body2" color="textSecondary" align="center">
                                                            O link foi enviado via {enviarEmail && enviarSms ? 'E-mail e SMS' : (enviarSms ? 'SMS' : 'E-mail')}.<br/>
                                                            Aguarde nesta tela até que o fornecedor conclua a assinatura.
                                                        </Typography>
                                                        <Alert 
                                                            severity="info"
                                                            sx={{ width: '100%', mt: 2 }}
                                                            action={
                                                                <Button color="inherit" size="small" onClick={() => navigator.clipboard.writeText(linkAssinatura)}>
                                                                    COPIAR LINK
                                                                </Button>
                                                            }
                                                        >
                                                            Link gerado: {linkAssinatura}
                                                            {updateSuccess && " (Dados de contato atualizados)"}
                                                        </Alert>
                                                    </Box>
                                                )}
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </>
                        )}

                        {/* Seção 5: Resumo do Lançamento */}
                        <Alert severity="info" icon={false} sx={{ mt: 1, '& .MuiAlert-message': { width: '100%' } }}>
                            <Grid container spacing={1}>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        <strong>Obra Referente:</strong> {selectedObra?.nome || lancamento.obra_nome}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        <strong>Valor Total Líquido:</strong> {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalValor)}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        <strong>Itens inclusos:</strong> {lancamento.items.length} ({mainDescricao})
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Alert>

                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Button onClick={onClose} color="inherit" sx={{ fontWeight: 'bold' }}>
                    {linkAssinatura ? 'Fechar' : 'Cancelar'}
                </Button>
                
                {modo === 'imprimir' && (
                    <Button 
                        variant="contained" 
                        startIcon={<PrintIcon />} 
                        onClick={handlePrint}
                        disabled={!isFormValid || loading}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Imprimir Recibo
                    </Button>
                )}

                {modo === 'assinatura' && !linkAssinatura && (
                    <Button 
                        variant="contained" 
                        color="primary"
                        startIcon={gerandoLink ? <CircularProgress size={20} color="inherit" /> : <EditNoteIcon />} 
                        onClick={handleGerarAssinatura}
                        disabled={!isAssinaturaValid || loading || gerandoLink}
                        sx={{ fontWeight: 'bold' }}
                    >
                        {gerandoLink ? 'Enviando...' : 'Gerar e Enviar Assinatura'}
                    </Button>
                )}
            </DialogActions>

            <div className="print-only" style={{ position: modo === 'assinatura' ? 'absolute' : 'static', left: '-9999px' }}>
                {selectedFornecedor && selectedInvestidor && selectedObra && (
                    <ReciboPrint
                        ref={componentRef}
                        fornecedor={selectedFornecedor}
                        investidor={selectedInvestidor}
                        obra={selectedObra}
                        valor={totalValor}
                        data={new Date(data + 'T12:00:00')}
                        descricao={mainDescricao}
                        cidade={cidade}
                        estado={estado}
                        tipoDocumentoBeneficiario={tipoDocumento}
                        pagador={selectedPagador}
                        quitaIntegralmente={quitaIntegralmente}
                        tipoRecibo={tipoRecibo}
                        itens={itensRecibo}
                    />
                )}
            </div>
        </Dialog>
    );
};

export default ReciboLancamentoModal;
